import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import settings from '../settings';
import createSubmitTraitsMediaAppearanceHandler from '../helpers/create-submit-traits-media-appearance-handler';
import controlIds from '../constants/control-ids';

const { Divider } = designSystem;
const {
  Context: ElementSettingsContext,
  components: { ToggleControl, SliderControl, ToggleableSectionControl },
  constants: { stages },
  helpers: { createBatch, generateInitialState },
  hooks: { useMountChunk },
} = elementSettings;

const AllMediaAppearanceSettingsContainer = ({ tabId, elementId, settingId }) => {
  const chunkId = tabId;
  const { callbacks, onUpdateState } = useContext(ElementSettingsContext);

  const controlSettings = useMemo(
    () => settings.getAllMediaAppearanceSettings(elementId, settingId),
    [elementId, settingId]
  );

  const handleUpdateState = useCallback(
    (batch) => {
      onUpdateState(batch, { chunkId });
    },
    [chunkId, onUpdateState]
  );

  const handleChange = useCallback(
    ({ controlId, elementIds, attributes, value, skipTracking = false }) => {
      const batch = createBatch({ elementIds, attributes, value });

      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleChange = useCallback(
    ({ controlId, elementIds, attributes, value }) => {
      const batch = createBatch({ elementIds, attributes, value });

      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleToggleableSectionChange = useCallback(
    ({ batch, controlId, value }) => {
      handleUpdateState(batch);
      callbacks.onToggleClick({ controlId, settingId, isActive: value });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const traitsHandler = useMemo(
    () =>
      createSubmitTraitsMediaAppearanceHandler({
        prefix: settingId,
      }),
    [settingId]
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    initialState: generateInitialState(Object.values(controlSettings)),
    stageHandlers: [
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
    withUnmount: true,
  });

  if (!isChunkMounted) return null;

  return (
    <>
      <ToggleControl {...controlSettings[controlIds.BORDER]} onChange={handleToggleChange} />
      <Divider appearance="light-grey" />
      <ToggleControl {...controlSettings[controlIds.SHADOW]} onChange={handleToggleChange} />
      <Divider appearance="light-grey" />
      <ToggleableSectionControl
        {...controlSettings[controlIds.CORNERS_SECTION]}
        onChange={handleToggleableSectionChange}
      >
        <SliderControl {...controlSettings[controlIds.CORNERS]} onChange={handleChange} />
      </ToggleableSectionControl>
    </>
  );
};

AllMediaAppearanceSettingsContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default AllMediaAppearanceSettingsContainer;
